// src/oura/ui/drawerHooks.ts
//
// The helpers a host needs to walk a group of items in one drawer: the
// sequence (what comes before and after, skipping what has gone), the nav
// the shell draws from it, drafts kept per item while moving, a field that
// takes focus instead of the title, and where focus goes on close.
//
// API
//   useDrawerSequence(ids, currentId, titleOf) -> DrawerSequence
//       ids: the snapshot taken when the drawer opened (the GroupFold's rows
//       in on-screen order, or the merchant's Needs-you queue). titleOf(id)
//       returns the item's title, or null to skip it (gone, or no longer in
//       the group). Gone items are skipped, never reordered. The current item
//       always counts, even once it has left its group, because the drawer
//       stays on it after a completing action.
//       -> { index (0-based, -1 when current is not in ids), total, prev, next, left, settled }
//       `left` is every live item but the current one: "2 left in Needs us".
//       `settled`: the current item has left its group, so the counter hides.
//   drawerSequence(...)  the same, as a plain function for event handlers.
//   drawerNav(seq, { group, onMove, keys?, disabled? }) -> DrawerNav | undefined
//       undefined when there is nothing to walk (not in the snapshot, or 1 item).
//   useDrawerFocusClaim() -> callback ref. Put it on the field a sheet's move
//       is typing into (merchant answer, Ask for a change, reply or write
//       mode): on open and on a move it takes focus instead of the title.
//       The sheet must be keyed per item so the claim is made on mount.
//   useDrawerDrafts() -> { get, set, clear, count }: unsent text per item id,
//       held by the host so it survives the keyed sheet remounting on a move.
//   returnFocusChain(...selectors) -> DrawerReturnFocus: the first selector
//       that matches on close. Pass the row, then its group head; the shell
//       falls back to the opener.
//   dataSel(name, value) -> '[data-name="value"]', value escaped.

import { useCallback, useContext, useMemo, useState } from "react";
import { DrawerSlotContext, type DrawerNav, type DrawerSeqItem } from "./drawerSlots";

export interface DrawerSequence {
    /** 0-based position of the current item among the live ones; -1 when it is not in the snapshot. */
    index: number;
    /** Live items, the current one included. */
    total: number;
    prev: DrawerSeqItem | null;
    next: DrawerSeqItem | null;
    /** Live items other than the current one. */
    left: number;
    /** The current item is no longer in the group (it counts in `total` only so the arrows still work). */
    settled: boolean;
}

type TitleOf = (id: string) => string | null | undefined;

export function drawerSequence(ids: readonly string[], currentId: string, titleOf: TitleOf): DrawerSequence {
    const live: DrawerSeqItem[] = [];
    let index = -1;
    for (const id of ids) {
        if (id === currentId) {
            index = live.length;
            live.push({ id, title: titleOf(id) ?? "" });
            continue;
        }
        const title = titleOf(id);
        if (title != null) live.push({ id, title });
    }
    return {
        index,
        total: live.length,
        prev: index > 0 ? live[index - 1] : null,
        next: index >= 0 && index < live.length - 1 ? live[index + 1] : null,
        left: index >= 0 ? live.length - 1 : live.length,
        settled: index >= 0 && titleOf(currentId) == null,
    };
}

/** See the file head. A plain computation: `titleOf` is usually a new function each render. */
export function useDrawerSequence(ids: readonly string[], currentId: string, titleOf: TitleOf): DrawerSequence {
    return drawerSequence(ids, currentId, titleOf);
}

export function drawerNav(
    seq: DrawerSequence,
    opts: { group: string; onMove: DrawerNav["onMove"]; keys?: boolean; disabled?: boolean },
): DrawerNav | undefined {
    if (seq.index < 0 || seq.total <= 1) return undefined;
    return {
        group: opts.group,
        index: seq.index,
        total: seq.total,
        prev: seq.prev,
        next: seq.next,
        onMove: opts.onMove,
        keys: opts.keys,
        disabled: opts.disabled,
        settled: seq.settled,
    };
}

/**
 * A callback ref for the field a sheet's move is typing into. On open and on
 * a move the shell focuses it instead of the title; at any other time it
 * does nothing, so a field that appears later never steals focus.
 */
export function useDrawerFocusClaim(): (el: HTMLElement | null) => void | (() => void) {
    const slots = useContext(DrawerSlotContext);
    const claim = slots?.claimFocus;
    return useCallback(
        (el: HTMLElement | null) => {
            if (!el || !claim) return;
            return claim(el);
        },
        [claim],
    );
}

export interface DrawerDrafts {
    get(id: string): string;
    set(id: string, text: string): void;
    clear(id: string): void;
    /** Items with unsent text, for "Kept. 2 answers not sent yet." */
    count: number;
}

/** Unsent text per item, kept by the host while the viewer moves through the drawer. */
export function useDrawerDrafts(): DrawerDrafts {
    const [map, setMap] = useState<ReadonlyMap<string, string>>(() => new Map());
    const set = useCallback((id: string, text: string) => {
        setMap((m) => {
            if ((m.get(id) ?? "") === text) return m;
            const next = new Map(m);
            if (text) next.set(id, text);
            else next.delete(id);
            return next;
        });
    }, []);
    const clear = useCallback((id: string) => {
        setMap((m) => {
            if (!m.has(id)) return m;
            const next = new Map(m);
            next.delete(id);
            return next;
        });
    }, []);
    return useMemo(() => {
        let count = 0;
        map.forEach((text) => {
            if (text.trim()) count += 1;
        });
        return { get: (id: string) => map.get(id) ?? "", set, clear, count };
    }, [map, set, clear]);
}

/** `[data-<name>="<value>"]`, with the value escaped for a selector. */
export function dataSel(name: string, value: string): string {
    return `[data-${name}="${CSS.escape(value)}"]`;
}

/**
 * Focus on close: the first selector that matches, looked up a frame after
 * the close. Pass the item's row, then its group head; with neither on the
 * page the shell goes back to whatever opened the drawer.
 */
export function returnFocusChain(...selectors: Array<string | null | undefined | false>): () => HTMLElement | null {
    return () => {
        for (const s of selectors) {
            if (!s) continue;
            const el = document.querySelector<HTMLElement>(s);
            if (el) return el;
        }
        return null;
    };
}
